import React, { useState } from "react";
import { usePodeEditar } from "@hooks/usePodeEditar";
import { fornecedorService } from "@services/fornecedorService";
import { Fornecedor, CreateFornecedorDTO } from "@models/index";
import {
  Button,
  Input,
  ServerDataTable,
  Column,
  DataTableAction,
} from "@components/common";
import { useServerPagination } from "@hooks/useServerPagination";
import { useFeedback } from "@contexts/FeedbackContext";
import {
  HiPlus,
  HiTrash,
  HiRefresh,
  HiPencil,
  HiX,
  HiDatabase,
  HiIdentification,
} from "react-icons/hi";
import "./pages-common.css";

export const Fornecedores: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingReceita, setSyncingReceita] = useState<string | null>(null);
  // Progresso do backfill de nome fantasia. É um laço longo por natureza — a
  // ReceitaWS gratuita aceita 3 consultas por minuto — então precisa mostrar
  // onde está e poder ser interrompido.
  const [buscandoFantasia, setBuscandoFantasia] = useState(false);
  const [progressoFantasia, setProgressoFantasia] = useState<{
    atualizados: number;
    restantes: number | null;
    aguardando?: boolean;
  }>({ atualizados: 0, restantes: null });
  const pararFantasiaRef = React.useRef(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateFornecedorDTO>({
    nome: "",
    email: "",
    telefone: "",
    cnpj: "",
    categoria: "",
    endereco: "",
  });
  const { confirm, showError, showSuccess } = useFeedback();
  // Visualizador consulta, sem cadastrar, editar, sincronizar nem excluir.
  const podeEditar = usePodeEditar();

  // Hook de paginação server-side
  const {
    data: fornecedores,
    loading,
    pagination,
    setPage,
    setPageSize,
    setSearch,
    setSortBy,
    setSortOrder,
    refresh,
  } = useServerPagination({
    fetchFunction: fornecedorService.getAll,
    initialPageSize: 10,
    initialSortBy: "nome",
    initialSortOrder: "asc",
  });

  const resetForm = () => {
    setFormData({
      nome: "",
      email: "",
      telefone: "",
      cnpj: "",
      categoria: "",
      endereco: "",
      nomeFantasia: "",
    });
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await fornecedorService.update(editingId, formData);
        showSuccess("Fornecedor atualizado com sucesso!");
      } else {
        await fornecedorService.create(formData);
        showSuccess("Fornecedor cadastrado com sucesso!");
      }
      resetForm();
      setShowForm(false);
      refresh();
    } catch (err) {
      console.error(err);
      showError(
        "Não foi possível salvar o fornecedor. Verifique os dados e tente novamente.",
      );
    }
  };

  const handleEdit = (fornecedor: Fornecedor) => {
    setFormData({
      nome: fornecedor.nome || "",
      email: fornecedor.email || "",
      telefone: fornecedor.telefone || "",
      cnpj: fornecedor.cnpj || "",
      categoria: fornecedor.categoria || "",
      endereco: fornecedor.endereco || "",
      nomeFantasia: fornecedor.nomeFantasia ?? "",
    });
    setEditingId(fornecedor.id);
    setShowForm(true);
  };

  const handleDelete = async (fornecedor: Fornecedor) => {
    const confirmed = await confirm({
      title: "Excluir fornecedor",
      message: `Tem certeza que deseja excluir "${fornecedor.nome}"? Esta ação não poderá ser desfeita.`,
      confirmText: "Excluir",
      cancelText: "Cancelar",
      tone: "danger",
    });

    if (!confirmed) return;

    try {
      await fornecedorService.delete(fornecedor.id);
      refresh();
      showSuccess("Fornecedor excluído com sucesso.");
    } catch (err) {
      console.error(err);
      showError("Não foi possível excluir este fornecedor.");
    }
  };

  const handleSyncReceita = async (fornecedor: Fornecedor) => {
    if (!fornecedor.cnpj) {
      showError("Este fornecedor não possui CNPJ cadastrado.");
      return;
    }

    try {
      setSyncingReceita(fornecedor.id);
      const atualizado = await fornecedorService.syncFromReceita(fornecedor.id);
      refresh();
      showSuccess(
        `"${atualizado.razaoSocial || atualizado.nome}" sincronizado com sucesso com a Receita Federal.`,
      );
    } catch (err: any) {
      showError(err?.message || "Erro ao sincronizar com a Receita Federal.");
    } finally {
      setSyncingReceita(null);
    }
  };

  const handleSyncWrike = async () => {
    const confirmed = await confirm({
      title: "Sincronizar com Wrike",
      message:
        "Deseja importar todos os fornecedores do Wrike? Esta operação usa sincronização em massa (bulk) e é muito rápida.",
      confirmText: "Sincronizar",
      cancelText: "Cancelar",
      tone: "default",
    });

    if (!confirmed) return;

    try {
      setSyncing(true);
      const result = await fornecedorService.syncFromWrike();

      refresh();
      const fantasias = result.nomesFantasiaAtualizados ?? 0;
      showSuccess(
        `Sincronização concluída! ${result.totalProcessed} fornecedores processados em ${result.tempoDecorrido} (${result.velocidade})` +
          (fantasias > 0
            ? ` · ${fantasias} nome(s) fantasia atualizado(s)`
            : ""),
      );
    } catch (err) {
      console.error(err);
      showError("Erro ao sincronizar com Wrike.");
    } finally {
      setSyncing(false);
    }
  };

  const handleBuscarNomesFantasia = async () => {
    if (buscandoFantasia) {
      // Segundo clique interrompe: o laço checa esta marca entre um lote e
      // outro, e o que já foi gravado permanece.
      pararFantasiaRef.current = true;
      return;
    }

    const confirmed = await confirm({
      title: "Buscar nomes fantasia",
      message:
        "Consulta o CNPJ de todos os fornecedores que estão sem nome fantasia e preenche o que encontrar.\n\n" +
        "Costuma levar poucos minutos, mesmo com a base inteira. Pode ser interrompido a qualquer momento, e o que já foi atualizado permanece — rodar de novo continua de onde parou.\n\n" +
        "Mantenha esta aba aberta enquanto roda.",
      confirmText: "Buscar",
      cancelText: "Cancelar",
      tone: "default",
    });

    if (!confirmed) return;

    pararFantasiaRef.current = false;
    setBuscandoFantasia(true);
    setProgressoFantasia({ atualizados: 0, restantes: null });

    let atualizados = 0;

    try {
      // Lotes de 100, e o servidor paraleliza dentro de cada um. O laço no
      // cliente existe para a requisição não ficar pendurada minutos a fio, e
      // para o progresso e a interrupção funcionarem entre um lote e outro.
      let restantesAnterior: number | null = null;

      for (;;) {
        if (pararFantasiaRef.current) break;

        const r = await fornecedorService.buscarNomesFantasia(100);
        atualizados += r.atualizados;
        setProgressoFantasia({ atualizados, restantes: r.aindaAmbiguos });

        if (r.processados === 0 || r.aindaAmbiguos === 0) break;

        // Limite de requisição não é erro, é pressa: o lote inteiro volta
        // barrado e basta esperar. Desistir aqui, como antes, obrigava a
        // recomeçar na mão sem nenhuma pista do motivo.
        if (r.deveAguardar) {
          setProgressoFantasia({
            atualizados,
            restantes: r.aindaAmbiguos,
            aguardando: true,
          });
          await new Promise((resolve) => setTimeout(resolve, 60000));
          setProgressoFantasia({
            atualizados,
            restantes: r.aindaAmbiguos,
            aguardando: false,
          });
          continue;
        }

        // Trava de segurança: se a fila não anda e não é limite de requisição,
        // parar em vez de repetir o mesmo lote indefinidamente.
        if (restantesAnterior !== null && r.aindaAmbiguos >= restantesAnterior) {
          showError(
            `A busca não avançou no último lote e foi interrompida. ${r.erros} erro(s) de consulta — verifique o log da API.`,
          );
          break;
        }
        restantesAnterior = r.aindaAmbiguos;
      }

      refresh();

      if (atualizados > 0) {
        showSuccess(
          `${atualizados} fornecedor(es) com nome fantasia atualizado.` +
            (pararFantasiaRef.current ? " Busca interrompida." : ""),
        );
      } else {
        showSuccess(
          pararFantasiaRef.current
            ? "Busca interrompida."
            : "Nenhum fornecedor precisava de atualização.",
        );
      }
    } catch (err: any) {
      console.error(err);
      showError(
        err?.message ||
          "Erro ao buscar os nomes fantasia. O que já foi atualizado permanece.",
      );
    } finally {
      setBuscandoFantasia(false);
      pararFantasiaRef.current = false;
    }
  };

  const handleSortChange = (sortByKey: string, order: "asc" | "desc") => {
    setSortBy(sortByKey);
    setSortOrder(order);
  };

  // Definir colunas da tabela
  const columns: Column<Fornecedor>[] = [
    {
      key: "nome",
      label: "Nome",
      sortable: true,
      width: "70%",
    },
    {
      key: "cnpj",
      label: "CNPJ",
      sortable: true,
      width: "25%",
      render: (f) => f.cnpj || "-",
    },
  ];

  // Definir ações da tabela
  const actions: DataTableAction<Fornecedor>[] = !podeEditar ? [] : [
    {
      icon: <HiPencil />,
      onClick: handleEdit,
      variant: "success",
      tooltip: "Editar fornecedor",
    },
    {
      icon: (f: Fornecedor) =>
        syncingReceita === f.id ? (
          <span className="loading loading-spinner loading-xs" />
        ) : (
          <HiDatabase />
        ),
      onClick: (f) => handleSyncReceita(f),
      variant: "secondary",
      tooltip: "Sincronizar com Receita Federal",
      disabled: (f) => syncingReceita === f.id || !f.cnpj,
    },
    {
      icon: <HiTrash />,
      onClick: handleDelete,
      variant: "danger",
      tooltip: "Excluir fornecedor",
    },
  ];

  return (
    <div className="crud-page">
      <div className="page-header">
        <h1>Fornecedores</h1>
        <p>Gerencie os fornecedores cadastrados no sistema</p>
      </div>

      {/* Formulário de cadastro/edição */}
      {showForm && (
        <div className="form-card">
          <div className="form-header">
            <h2>{editingId ? "Editar Fornecedor" : "Novo Fornecedor"}</h2>
            <Button
              variant="secondary"
              size="small"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
            >
              <HiX />
            </Button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <Input
                label="Nome *"
                value={formData.nome}
                onChange={(e) =>
                  setFormData({ ...formData, nome: e.target.value })
                }
                required
              />
              <Input
                label="Nome Fantasia"
                value={formData.nomeFantasia || ""}
                onChange={(e) =>
                  setFormData({ ...formData, nomeFantasia: e.target.value })
                }
                helperText="Usado no FACITE. Deixe em branco se a empresa não tiver."
              />
              <Input
                label="CNPJ"
                value={formData.cnpj || ""}
                onChange={(e) =>
                  setFormData({ ...formData, cnpj: e.target.value })
                }
              />
              <Input
                label="E-mail *"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
              <Input
                label="Telefone"
                value={formData.telefone || ""}
                onChange={(e) =>
                  setFormData({ ...formData, telefone: e.target.value })
                }
              />
              <Input
                label="Categoria"
                value={formData.categoria || ""}
                onChange={(e) =>
                  setFormData({ ...formData, categoria: e.target.value })
                }
              />
              <Input
                label="Endereço"
                value={formData.endereco || ""}
                onChange={(e) =>
                  setFormData({ ...formData, endereco: e.target.value })
                }
                fullWidth
              />
            </div>
            <div className="form-actions">
              <Button type="submit" variant="primary">
                {editingId ? "Atualizar" : "Salvar"} Fornecedor
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Tabela com paginação server-side */}
      <ServerDataTable
        data={fornecedores}
        columns={columns}
        actions={actions}
        loading={loading}
        pagination={pagination}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome, email, CNPJ, telefone, categoria..."
        onSortChange={handleSortChange}
        keyExtractor={(fornecedor) => fornecedor.id}
        emptyMessage="Nenhum fornecedor cadastrado ainda"
        headerActions={
          podeEditar && (
          <>
            <Button
              variant="success"
              onClick={handleSyncWrike}
              disabled={syncing || buscandoFantasia}
            >
              <HiRefresh className={syncing ? "spinning" : ""} />
              {syncing ? "Sincronizando..." : "Sincronizar Wrike"}
            </Button>
            <Button
              variant="secondary"
              onClick={handleBuscarNomesFantasia}
              disabled={syncing}
              title="Consulta o CNPJ para preencher o nome fantasia de quem está sem"
            >
              <HiIdentification className={buscandoFantasia ? "spinning" : ""} />
              {buscandoFantasia
                ? `${
                    progressoFantasia.aguardando
                      ? "Aguardando limite..."
                      : "Buscando..."
                  } ${progressoFantasia.atualizados} ok${
                    progressoFantasia.restantes !== null
                      ? ` · ${progressoFantasia.restantes} restantes`
                      : ""
                  } (parar)`
                : "Buscar nomes fantasia"}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              disabled={showForm || buscandoFantasia}
            >
              <HiPlus />
              Novo Fornecedor
            </Button>
          </>
          )
        }
      />
    </div>
  );
};
